import { View, StyleSheet, TouchableOpacity, Text as RNText } from 'react-native';
import { Square, Camera } from 'lucide-react-native';
import theme from '@/constants/colors';
import { Typography } from '@/constants/typography';
import { useTranslation } from 'react-i18next';

type Props = {
  isRecording: boolean;
  isExpanded: boolean;
  duration: number;
  distance: number;
  elevation: number;
  sniffDuration: number;
  pace: string;
  speed: number;
  progress: number;
  showProgress: boolean;
  startLabel?: string;
  accuracy?: number; // Add this prop to determine signal strength
  onStart?: () => void;
  onStop: () => void;
  onClose?: () => void;
  onCancel?: () => void;
  onCamera?: () => void;
};

// Helper to determine signal strength
const getSignalData = (accuracy?: number) => {
  if (accuracy === undefined || accuracy <= 0) return { label: 'SEARCHING', color: '#666', bars: 0 };
  if (accuracy <= 10) return { label: 'EXCELLENT', color: '#4CAF50', bars: 3 }; // High Accuracy
  if (accuracy <= 30) return { label: 'GOOD', color: '#FFC107', bars: 2 };      // Medium
  return { label: 'POOR', color: '#F44336', bars: 1 };                         // Low
};

export default function RecordOverlay({
  isRecording,
  isExpanded,
  duration,
  distance,
  elevation,
  sniffDuration,
  pace,
  speed,
  progress,
  showProgress,
  startLabel,
  accuracy,
  onStart,
  onStop,
  onClose,
  onCancel,
  onCamera,
}: Props) {
  const formattedDistance = `${(distance / 1000).toFixed(2)}km`;
  const formattedElevation = `${Math.round(elevation)}m`;
  const { t } = useTranslation();
  const signal = getSignalData(accuracy);

  if (!isRecording) {
    return (
      <View style={styles.container}>
        <View style={styles.signalWrapper}>
          {/* <RNText style={[styles.signalLabel, { color: signal.color }]}>{signal.label}</RNText> */}
          {/* <View style={styles.barsRow}>
            <View style={[styles.bar, { height: 6, backgroundColor: signal.bars >= 1 ? signal.color : '#333' }]} />
            <View style={[styles.bar, { height: 10, backgroundColor: signal.bars >= 2 ? signal.color : '#333' }]} />
            <View style={[styles.bar, { height: 14, backgroundColor: signal.bars >= 3 ? signal.color : '#333' }]} />
          </View> */}
        </View>
        <TouchableOpacity style={styles.recordButton} onPress={onStart} activeOpacity={0.8}>
          <RNText style={styles.recordButtonText}>{startLabel ?? t('record.startTrail')}</RNText>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* <View style={styles.signalWrapper}>
        <RNText style={[styles.signalLabel, { color: signal.color }]}>{signal.label}</RNText>
        <View style={styles.barsRow}>
          <View style={[styles.bar, { height: 6, backgroundColor: signal.bars >= 1 ? signal.color : '#333' }]} />
          <View style={[styles.bar, { height: 10, backgroundColor: signal.bars >= 2 ? signal.color : '#333' }]} />
          <View style={[styles.bar, { height: 14, backgroundColor: signal.bars >= 3 ? signal.color : '#333' }]} />
        </View>
      </View> */}
      {isExpanded ? (
        <View style={styles.grid}>

          <View style={styles.card}>
            <RNText style={styles.cardLabel}>{t('walkSummary.time')}</RNText>
            <RNText style={styles.cardValue}>{Math.floor(duration / 60)}m</RNText>
          </View>
          <View style={styles.card}>
            <RNText style={styles.cardLabel}>{t('walkSummary.distance')}</RNText>
            <RNText style={styles.cardValue}>{formattedDistance}</RNText>
          </View>
          <View style={styles.card}>
            <RNText style={styles.cardLabel}>{t('walkSummary.sniffTime')}</RNText>
            <RNText style={styles.cardValue}>
              {sniffDuration > 60 
                ? `${Math.floor(sniffDuration / 60)}m ${sniffDuration % 60}s` 
                : `${sniffDuration}s`}
            </RNText>
          </View>
          <View style={styles.card}>
            <RNText style={styles.cardLabel}>{t('walkSummary.elevation')}</RNText>
            <RNText style={styles.cardValue}>{formattedElevation}</RNText>
          </View>
        </View>
      ) : (
        <View style={styles.compactContainer}>
          <View style={styles.compactCard}>
            <RNText style={styles.cardLabel}>{t('walkSummary.time')}</RNText>
            <RNText style={styles.cardValue}>{Math.floor(duration / 60)}m</RNText>
          </View>
          <View style={styles.compactCard}>
            <RNText style={styles.cardLabel}>{t('walkSummary.distance')}</RNText>
            <RNText style={styles.cardValue}>{formattedDistance}</RNText>
          </View>
        </View>
      )}

      <View style={styles.controlsRow}>
        {onCancel ? (
          <TouchableOpacity style={styles.cancelButton} onPress={onCancel} activeOpacity={0.8}>
            <RNText style={styles.cancelText}>{t('common.cancel')}</RNText>
          </TouchableOpacity>
        ) : null}
        <TouchableOpacity style={styles.stopButton} onPress={onStop} activeOpacity={0.85}>
          <View style={styles.stopInner}>
            <Square size={18} color={theme.backgroundPrimary} fill={theme.backgroundPrimary} />
            <RNText style={styles.stopText}>{t('record.stop')}</RNText>
          </View>
        </TouchableOpacity>
        {isExpanded && onCamera ? (
          <TouchableOpacity style={styles.cameraButton} onPress={() => { if (onCamera) onCamera(); }} activeOpacity={0.8}>
            <Camera size={20} color={theme.accentPrimary} />
          </TouchableOpacity>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    paddingTop: 4,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 18,
  },
  card: {
    width: '48%',
    backgroundColor: theme.backgroundPrimary,
    borderRadius: 14,
    paddingVertical: 18,
    paddingHorizontal: 14,
    alignItems: 'flex-start',
    borderWidth: 2,
    borderColor: theme.borderSubtle,
  },
  cardLabel: {
    ...Typography.caption(theme.textMuted),
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  cardValue: {
    ...Typography.h3(theme.accentPrimary),
  },
  controlsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  recordButton: {
    backgroundColor: theme.accentSecondary,
    borderRadius: 16,
    paddingVertical: 14,
    paddingHorizontal: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stopButton: {
    flex: 2,
    backgroundColor: theme.accentSecondary,
    borderRadius: 18,
    paddingVertical: 18,
    paddingHorizontal: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stopInner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  stopText: {
    ...Typography.button(theme.backgroundPrimary),
    marginLeft: 8,
  },
  cameraButton: {
    width: 56,
    height: 56,
    borderRadius: 14,
    backgroundColor: theme.backgroundSecondary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  compactRow: {
    height: 44,
  },
  compactContainer: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 12,
    paddingHorizontal: 4,
  },
  compactCard: {
    flex: 1,
    backgroundColor: theme.backgroundPrimary,
    borderRadius: 14,
    paddingVertical: 12,
    paddingHorizontal: 12,
    alignItems: 'flex-start',
    borderWidth: 2,
    borderColor: theme.borderSubtle,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 12,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: theme.backgroundSecondaryVarient,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelText: {
    ...Typography.label(theme.textMuted),
  },
  recordButtonText: {
    ...Typography.button(theme.backgroundPrimary),
  },
  signalWrapper: {
    alignSelf: 'flex-end',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.6)',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    marginBottom: 12,
  },
  signalLabel: {
    fontSize: 10,
    fontWeight: 'bold',
    marginRight: 6,
    letterSpacing: 0.5,
  },
  barsRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 2,
  },
  bar: {
    width: 3,
    borderRadius: 1,
  }
});
